import { useSyncExternalStore } from "react";
import { isFirebaseConfigured, loadFirebase } from "./firebase";
import { followStudy } from "./studySync";
import { connectStudyCloud } from "./studyCloud";

/*
 * Who is signed in, and their profile -- read anywhere through useAccount(),
 * the same way useMyStudy works: a small module store rather than a context
 * provider around the whole app.
 *
 *   status  "off"        accounts are not configured; the site behaves as it
 *                        always has and My Study stays in this browser
 *           "loading"    the SDK is starting and has not said who this is yet
 *           "signedOut"
 *           "signedIn"
 *   user    { uid, email } while signed in
 *   profile users/{uid}: { email, firstName, lastName, biblePreference, ... }
 *
 * Passwords go straight to Firebase Authentication, which stores them hashed.
 * Nothing in this site's code or database ever keeps one.
 */

const OPEN_EVENT = "ctb:openAccount";

/*
 * "Keep me signed in on this device". Ticked, the sign-in survives closing
 * the browser, as on any personal device. Unticked -- the default, for a
 * library or coffee-shop computer -- it lasts only as long as the tab, and an
 * hour with no activity signs the reader out as well, in case they walk away
 * with the tab still open.
 */
const STAY_KEY = "ctbStaySignedIn";
const ACTIVE_KEY = "ctbLastActive";
const IDLE_LIMIT = 60 * 60 * 1000;

let state = {
  status: isFirebaseConfigured ? "loading" : "off",
  user: null,
  profile: null,
};
const listeners = new Set();
let services = null;
let stopProfile = null;

function setState(patch) {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

export function getAccount() {
  return state;
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useAccount() {
  return useSyncExternalStore(subscribe, getAccount, getAccount);
}

/* ---- Signing out after an hour away ------------------------------------- */

function staysSignedIn() {
  try {
    return window.localStorage.getItem(STAY_KEY) === "1";
  } catch (error) {
    return true;
  }
}

function setStaysSignedIn(stay) {
  try {
    if (stay) window.localStorage.setItem(STAY_KEY, "1");
    else window.localStorage.removeItem(STAY_KEY);
  } catch (error) {
    // nothing to remember it in; the session sign-in still applies
  }
}

/* Kept in sessionStorage, so a reload after an hour away still counts as an
   hour away rather than starting the clock again. */
function lastActive() {
  try {
    return Number(window.sessionStorage.getItem(ACTIVE_KEY)) || 0;
  } catch (error) {
    return 0;
  }
}

function markActive() {
  try {
    window.sessionStorage.setItem(ACTIVE_KEY, String(Date.now()));
  } catch (error) {
    // without storage the timer falls back to this page's own lifetime
  }
}

let stopIdleWatch = () => {};

function startIdleWatch() {
  stopIdleWatch();
  if (staysSignedIn()) return;

  if (!lastActive()) markActive();

  function check() {
    if (Date.now() - lastActive() > IDLE_LIMIT) idleSignOut();
  }

  /* Writing on every scroll event would be wasteful; once every 30 seconds
     is plenty for an hour's limit. */
  let lastMarked = 0;
  function activity() {
    const now = Date.now();
    if (now - lastMarked < 30000) return;
    lastMarked = now;
    markActive();
  }

  /* Browsers slow timers in a background tab, so coming back to the tab
     checks first -- before that visit counts as activity. */
  function visible() {
    if (document.visibilityState !== "visible") return;
    check();
    activity();
  }

  check();
  const timer = window.setInterval(check, 60 * 1000);
  const events = ["pointerdown", "keydown", "scroll", "touchstart"];
  events.forEach((name) =>
    window.addEventListener(name, activity, { passive: true })
  );
  document.addEventListener("visibilitychange", visible);

  stopIdleWatch = () => {
    window.clearInterval(timer);
    events.forEach((name) => window.removeEventListener(name, activity));
    document.removeEventListener("visibilitychange", visible);
    stopIdleWatch = () => {};
  };
}

function idleSignOut() {
  stopIdleWatch();
  signOut()
    .then(() =>
      openAccount({
        mode: "signIn",
        reason:
          "You were signed out after an hour without activity, so no one else using this computer can open your notes.",
      })
    )
    .catch((error) => console.warn("Account: sign-out failed", error));
}

/**
 * Starts following sign-in, once, at startup. While accounts are off this
 * downloads nothing.
 */
export function startAccount() {
  loadFirebase().then((loaded) => {
    if (!loaded) {
      setState({ status: "off" });
      return;
    }

    services = loaded;
    const { authApi, auth, db, firestoreApi } = loaded;

    authApi.onAuthStateChanged(auth, (user) => {
      if (stopProfile) stopProfile();
      stopProfile = null;

      /* My Study switches first, so anything waiting on sign-in (a Save
         pressed before signing in) writes to the account, not the browser. */
      followStudy(loaded, user);

      if (!user) {
        stopIdleWatch();
        setState({ status: "signedOut", user: null, profile: null });
        return;
      }

      startIdleWatch();

      setState({
        status: "signedIn",
        user: { uid: user.uid, email: user.email || "" },
        profile: state.user?.uid === user.uid ? state.profile : null,
      });

      stopProfile = firestoreApi.onSnapshot(
        firestoreApi.doc(db, "users", user.uid),
        (snapshot) => {
          setState({ profile: snapshot.exists() ? snapshot.data() : null });
        },
        (error) => console.warn("Account: could not load your profile", error)
      );
    });
  });
}

/* ---- Saving needs an account ------------------------------------------- */

/**
 * True when saving to My Study should ask the reader to sign in first. Off
 * when accounts are not configured, so the site keeps working the old way
 * until they are.
 */
export function needsAccountToSave() {
  return state.status === "loading" || state.status === "signedOut";
}

/*
 * Opens the account window. `then` runs once the reader is signed in -- the
 * Save they pressed, so it is not lost to the detour. Only the latest one is
 * kept: pressing a second Save while the window is open replaces the first.
 */
let pending = null;

export function openAccount({ mode = "signIn", reason = "", then = null } = {}) {
  pending = typeof then === "function" ? then : null;
  window.dispatchEvent(
    new CustomEvent(OPEN_EVENT, { detail: { mode, reason } })
  );
}

export function onOpenAccount(handler) {
  const listener = (event) => handler(event.detail || {});
  window.addEventListener(OPEN_EVENT, listener);
  return () => window.removeEventListener(OPEN_EVENT, listener);
}

export function runPending() {
  const action = pending;
  pending = null;
  if (action) action();
}

export function dropPending() {
  pending = null;
}

/* ---- Actions ------------------------------------------------------------- */

async function ready() {
  const loaded = services || (await loadFirebase());
  if (!loaded) throw Object.assign(new Error("off"), { code: "accounts/off" });
  return loaded;
}

function clean(value, max) {
  return String(value || "").trim().slice(0, max);
}

/* Sets how long the coming sign-in lasts; must run before it. */
async function choosePersistence({ auth, authApi }, stay) {
  setStaysSignedIn(stay);
  markActive();
  await authApi.setPersistence(
    auth,
    stay ? authApi.indexedDBLocalPersistence : authApi.browserSessionPersistence
  );
}

export async function signUp({
  firstName,
  lastName,
  email,
  password,
  biblePreference,
  staySignedIn = false,
}) {
  const loaded = await ready();
  const { auth, authApi, db, firestoreApi } = loaded;
  await choosePersistence(loaded, staySignedIn);
  const credential = await authApi.createUserWithEmailAndPassword(
    auth,
    clean(email, 254),
    password
  );
  const profile = {
    email: credential.user.email || clean(email, 254),
    firstName: clean(firstName, 80),
    lastName: clean(lastName, 80),
    biblePreference: clean(biblePreference, 20) || "NIV",
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  await Promise.all([
    firestoreApi.setDoc(firestoreApi.doc(db, "users", credential.user.uid), profile),
    authApi.updateProfile(credential.user, {
      displayName: `${profile.firstName} ${profile.lastName}`.trim(),
    }),
  ]);
}

export async function signIn({ email, password, staySignedIn = false }) {
  const loaded = await ready();
  const { auth, authApi } = loaded;
  await choosePersistence(loaded, staySignedIn);
  await authApi.signInWithEmailAndPassword(auth, clean(email, 254), password);
}

export async function signOut() {
  const { auth, authApi } = await ready();
  await authApi.signOut(auth);
  setStaysSignedIn(false);
  try {
    window.sessionStorage.removeItem(ACTIVE_KEY);
  } catch (error) {
    // nothing stored, nothing to clear
  }
}

/**
 * Deletes the account and everything in it: every saved item and note, path
 * progress, the profile, and the sign-in itself. Firebase only deletes a
 * sign-in that was used in the last few minutes, so the password is asked for
 * again first -- which also makes sure it is the account's owner at the
 * keyboard, not whoever sat down at a computer left signed in.
 *
 * The data goes before the sign-in: the database rules let only the signed-in
 * owner delete it, so once the sign-in is gone nothing could.
 */
export async function deleteAccount(password) {
  const { auth, authApi, db, firestoreApi } = await ready();
  const user = auth.currentUser;
  if (!user) throw Object.assign(new Error("signed out"), { code: "auth/no-current-user" });

  await authApi.reauthenticateWithCredential(
    user,
    authApi.EmailAuthProvider.credential(user.email, password)
  );

  await connectStudyCloud({ db, firestoreApi, uid: user.uid }).adapter.clearAll();
  await firestoreApi.deleteDoc(firestoreApi.doc(db, "users", user.uid));
  await authApi.deleteUser(user);

  stopIdleWatch();
  setStaysSignedIn(false);
  try {
    window.sessionStorage.removeItem(ACTIVE_KEY);
  } catch (error) {
    // nothing stored, nothing to clear
  }
}

export async function sendPasswordReset(email) {
  const { auth, authApi } = await ready();
  await authApi.sendPasswordResetEmail(auth, clean(email, 254));
}

/** Changes profile fields; quietly does nothing while signed out. */
export async function updateAccountProfile(changes) {
  if (state.status !== "signedIn" || !state.user) return;

  const { db, firestoreApi } = await ready();
  const next = { ...changes, updatedAt: Date.now() };
  if ("firstName" in next) next.firstName = clean(next.firstName, 80);
  if ("lastName" in next) next.lastName = clean(next.lastName, 80);
  if ("biblePreference" in next) next.biblePreference = clean(next.biblePreference, 20);

  await firestoreApi.setDoc(
    firestoreApi.doc(db, "users", state.user.uid),
    next,
    { merge: true }
  );
}

/* Firebase's error codes, in words a reader can act on. */
const MESSAGES = {
  "auth/email-already-in-use":
    "There is already an account with that email. Try signing in instead.",
  "auth/invalid-email": "That email address does not look right.",
  "auth/invalid-credential": "That email and password do not match an account.",
  "auth/wrong-password": "That email and password do not match an account.",
  "auth/user-not-found": "That email and password do not match an account.",
  "auth/missing-password": "Enter your password.",
  "auth/weak-password": "Choose a password of at least 8 characters.",
  "auth/too-many-requests":
    "Too many tries. Wait a few minutes, or reset your password.",
  "auth/network-request-failed":
    "Could not reach the server. Check your connection and try again.",
  "auth/requires-recent-login": "For your security, sign out, sign back in, and try again.",
  "auth/no-current-user": "You are already signed out.",
  "delete/wrong-password": "That password is not right.",
  "accounts/off": "Accounts are not available right now.",
};

export function accountErrorMessage(error) {
  return (
    MESSAGES[error?.code] ||
    "Something went wrong. Please try again in a moment."
  );
}
